import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import {
  CREATE_DEALER,
  GET_MY_DEALER,
  UPDATE_DEALER,
} from '../../../services/dealerService/queries';
import { useMutation, useQuery } from '@apollo/client';
import { IDealer } from '../../../../__generated__/graphql';
import { toast } from 'react-toastify';

const Container = styled.div`
  ${tw`w-full max-w-4xl mx-auto p-8 bg-white rounded-lg box-border md:shadow-md `}
`;

const Header = styled.div`
  ${tw`flex justify-between items-center mb-6`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold`}
`;

const EditButton = styled.button`
  ${tw`p-2 text-blue-600`}
  &:hover {
    ${tw`text-blue-800`}
  }
`;

const SaveButton = styled.button`
  ${tw`px-4 py-2 bg-blue-600 text-white rounded`}
  &:hover {
    ${tw`bg-blue-700`}
  }
`;

const InfoGrid = styled.div`
  ${tw`grid grid-cols-2 gap-6`}
`;

const InfoSection = styled.div`
  ${tw`mb-4`}
`;

const Label = styled.div`
  ${tw`text-sm font-semibold text-gray-600 mb-1`}
`;

const Text = styled.div`
  ${tw`text-base`}
`;

const Input = styled.input`
  ${tw`w-full p-2 border border-gray-300 rounded focus:border-blue-500 focus:outline-none`}
`;

const TextArea = styled.textarea`
  ${tw`w-full p-2 border border-gray-300 rounded focus:border-blue-500 focus:outline-none`}
`;

type DealerInfoState = {
  name: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  certificateNumber: string;
  certificateExpiresAt: string;
  phone: string;
  email: string;
  website: string;
  introduction: string;
};

const DealerInfo = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [dealerInfo, setDealerInfo] = useState<DealerInfoState>({
    name: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    certificateNumber: '',
    certificateExpiresAt: '',
    phone: '',
    email: '',
    website: '',
    introduction: '',
  });

  const {
    data: dealerData,
    loading,
    error,
  } = useQuery(GET_MY_DEALER, {
    fetchPolicy: 'cache-first',
    onCompleted: data => {
      if (data.myDealer.id) {
        setDealerInfo({
          name: data.myDealer.name,
          streetAddress: data.myDealer.streetAddress,
          city: data.myDealer.city,
          state: data.myDealer.state,
          zipCode: data.myDealer.zipCode,
          certificateNumber: data.myDealer.certificateNumber,
          certificateExpiresAt: data.myDealer.certificateExpiresAt,
          phone: data.myDealer.phone,
          email: data.myDealer.email,
          website: data.myDealer.website,
          introduction: data.myDealer.introduction,
        });
      }
    },
  });

  const [updateDealer, { loading: updateDealerLoading }] = useMutation(UPDATE_DEALER, {
    refetchQueries: [GET_MY_DEALER],
    onCompleted: () => {
      toast.success('Dealer info updated successfully');
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const [createDealer, { loading: createDealerLoading }] = useMutation(CREATE_DEALER, {
    refetchQueries: [GET_MY_DEALER],
    onCompleted: () => {
      toast.success('Dealer info created successfully');
    },
    onError: error => {
      toast.error(error.message);
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (dealerData?.myDealer?.id) {
      updateDealer({
        variables: {
          updateDealerInput: { ...dealerInfo, id: dealerData.myDealer.id },
        },
      });
    } else {
      createDealer({
        variables: {
          createDealerInput: dealerInfo,
        },
      });
    }
    setIsEditing(false);
  };

  const handleChange = (field: keyof Partial<IDealer>, value: string) => {
    setDealerInfo(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Container>
      <Header>
        <Title>Dealer Information</Title>
        {isEditing ? (
          <SaveButton onClick={handleSave}>Save Changes</SaveButton>
        ) : (
          <EditButton onClick={handleEdit}>
            <FontAwesomeIcon icon={faPencil} />
          </EditButton>
        )}
      </Header>

      <InfoGrid>
        <InfoSection>
          <Label>Dealer Name</Label>
          {isEditing ? (
            <Input
              value={dealerInfo.name ?? ''}
              onChange={e => handleChange('name', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.name ?? ''}</Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>Street Address</Label>
          {isEditing ? (
            <Input
              value={dealerInfo.streetAddress ?? ''}
              onChange={e => handleChange('streetAddress', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.streetAddress ?? ''}</Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>City</Label>
          {isEditing ? (
            <Input
              value={dealerInfo.city ?? ''}
              onChange={e => handleChange('city', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.city ?? ''}</Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>State</Label>
          {isEditing ? (
            <Input
              value={dealerInfo.state ?? ''}
              onChange={e => handleChange('state', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.state ?? ''}</Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>ZIP Code</Label>
          {isEditing ? (
            <Input
              value={dealerInfo.zipCode ?? ''}
              onChange={e => handleChange('zipCode', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.zipCode ?? ''}</Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>Certificate Number</Label>
          {isEditing ? (
            <Input
              value={dealerInfo.certificateNumber ?? ''}
              onChange={e => handleChange('certificateNumber', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.certificateNumber ?? ''}</Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>Certificate Expiration</Label>
          {isEditing ? (
            <Input
              type="date"
              value={dealerInfo.certificateExpiresAt ?? ''}
              onChange={e => handleChange('certificateExpiresAt', e.target.value)}
            />
          ) : (
            <Text>
              {dealerInfo.certificateExpiresAt &&
                new Date(dealerInfo.certificateExpiresAt).toLocaleDateString()}
            </Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>Phone</Label>
          {isEditing ? (
            <Input
              value={dealerInfo.phone ?? ''}
              onChange={e => handleChange('phone', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.phone ?? ''}</Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>Email</Label>
          {isEditing ? (
            <Input
              type="email"
              value={dealerInfo.email ?? ''}
              onChange={e => handleChange('email', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.email ?? ''}</Text>
          )}
        </InfoSection>

        <InfoSection>
          <Label>Website</Label>
          {isEditing ? (
            <Input
              value={dealerInfo.website ?? ''}
              onChange={e => handleChange('website', e.target.value)}
            />
          ) : (
            <Text>{dealerInfo.website ?? ''}</Text>
          )}
        </InfoSection>
      </InfoGrid>

      <InfoSection>
        <Label>Introduction</Label>
        {isEditing ? (
          <TextArea
            rows={4}
            value={dealerInfo.introduction ?? ''}
            onChange={e => handleChange('introduction', e.target.value)}
          />
        ) : (
          <Text>{dealerInfo.introduction ?? ''}</Text>
        )}
      </InfoSection>
    </Container>
  );
};

export default DealerInfo;
